import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { getRequest } from 'api/requests';
import { RootState } from 'store/store';
import { sortBy } from 'lodash';
import { ILibrariesState } from './interfaces';
import { IUserType, IRole, ILogType } from 'interfaces/main';

// Селекторы libraries
export const LibrariesSelector = (state: RootState): ILibrariesState =>
  state.libraries;

export const LibrariesLoadingSelector = createSelector(
  LibrariesSelector,
  ({ isLoading }) => isLoading,
);

// Загрузка списка типов пользователей
export const fetchUserTypes = createAsyncThunk(
  'libraries/fetchUserTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest('userType', {}, true);

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Загрузка списка типов логов
export const fetchLogTypes = createAsyncThunk(
  'libraries/fetchLogTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest('logType', {}, true);

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Загрузка списка ролей
export const fetchRoles = createAsyncThunk(
  'libraries/fetchRoles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest('role', {}, true);

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Загрузка списка категорий
export const fetchCategories = createAsyncThunk(
  'libraries/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest('category', {}, true);

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Загрузка списка удобств
export const fetchAmenities = createAsyncThunk(
  'libraries/fetchAmenities',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest('amenity', {}, true);

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Загрузка списка городов
// export const fetchCities = createAsyncThunk(
//   'libraries/fetchCities',
//   async (_, { rejectWithValue }) => {
//     try {
//       const response = await getRequest('city', {}, true);

//       return response.data.map(mapCity);
//     } catch (error) {
//       return rejectWithValue(error);
//     }
//   },
// );

// Загрузка списка стран
export const fetchCountries = createAsyncThunk(
  'libraries/fetchCountries',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest('country', {}, true);

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Загрузка списка назначений
export const fetchPurposes = createAsyncThunk(
  'libraries/fetchPurposes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest('purpose', {}, true);

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Загрузка списка локаций
export const fetchLocations = createAsyncThunk(
  'libraries/fetchLocations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest('location', {}, true);

      return sortBy(response.data, 'name');
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Загрузка статусов мебелизированности
export const fetchFurnishingStatuses = createAsyncThunk(
  'libraries/fetchFurnishingStatuses',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest('furnishingStatus', {}, true);

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Загрузка статусов выполнения
export const fetchCompletedStatuses = createAsyncThunk(
  'libraries/fetchCompletedStatuses',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest('completedStatus', {}, true);

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Загрузка типов лота
export const fetchTypes = createAsyncThunk(
  'libraries/fetchTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest('type', {}, true);

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Загрузка регионов
export const fetchRegion = createAsyncThunk(
  'libraries/fetchRegion',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest('region', {}, true);

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Загрузка периодов аренды
export const fetchRentFrequencies = createAsyncThunk(
  'libraries/fetchRentFrequencies',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest('rentFrequency', {}, true);

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const initialState: ILibrariesState = {
  isLoading: false,
  categories: [],
  userTypes: [],
  roles: [],
  logTypes: [],
  amenities: [],
  regions: [],
  countries: [],
  types: [],
  rentFrequencies: [],
  purposes: [],
  completedStatuses: [],
  furnishingStatuses: [],
  locations: [],
};

const LibrariesSlice = createSlice({
  name: 'libraries',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserTypes.pending, (state) => ({
        ...state,
        isLoading: true,
      }))
      .addCase(fetchUserTypes.rejected, (state) => ({
        ...state,
        isLoading: false,
      }))
      .addCase(fetchUserTypes.fulfilled, (state, { payload }) => ({
        ...state,
        userTypes: payload.sort((a: IUserType, b: IUserType) =>
          a.name > b.name ? 1 : -1,
        ),
        isLoading: false,
      }))
      .addCase(fetchLogTypes.pending, (state) => ({
        ...state,
        isLoading: true,
      }))
      .addCase(fetchLogTypes.rejected, (state) => ({
        ...state,
        isLoading: false,
      }))
      .addCase(fetchLogTypes.fulfilled, (state, { payload }) => ({
        ...state,
        logTypes: payload.sort((a: ILogType, b: ILogType) =>
          a.name > b.name ? 1 : -1,
        ),
        isLoading: false,
      }))
      .addCase(fetchRoles.pending, (state) => ({
        ...state,
        isLoading: true,
      }))
      .addCase(fetchRoles.rejected, (state) => ({
        ...state,
        isLoading: false,
      }))
      .addCase(fetchRoles.fulfilled, (state, { payload }) => ({
        ...state,
        roles: payload.sort((a: IRole, b: IRole) => (a.name > b.name ? 1 : -1)),
        isLoading: false,
      }))
      .addCase(fetchCategories.pending, (state) => ({
        ...state,
        isLoading: true,
      }))
      .addCase(fetchCategories.rejected, (state) => ({
        ...state,
        isLoading: false,
      }))
      .addCase(fetchCategories.fulfilled, (state, { payload }) => ({
        ...state,
        categories: payload,
        isLoading: false,
      }))
      // .addCase(fetchCities.pending, (state) => ({
      //   ...state,
      //   isLoading: true,
      // }))
      // .addCase(fetchCities.rejected, (state) => ({
      //   ...state,
      //   isLoading: false,
      // }))
      // .addCase(fetchCities.fulfilled, (state, { payload }) => ({
      //   ...state,
      //   cities: payload,
      //   isLoading: false,
      // }))
      .addCase(fetchCountries.pending, (state) => ({
        ...state,
        isLoading: true,
      }))
      .addCase(fetchCountries.rejected, (state) => ({
        ...state,
        isLoading: false,
      }))
      .addCase(fetchCountries.fulfilled, (state, { payload }) => ({
        ...state,
        countries: payload,
        isLoading: false,
      }))
      .addCase(fetchAmenities.pending, (state) => ({
        ...state,
        isLoading: true,
      }))
      .addCase(fetchAmenities.rejected, (state) => ({
        ...state,
        isLoading: false,
      }))
      .addCase(fetchAmenities.fulfilled, (state, { payload }) => ({
        ...state,
        amenities: payload,
        isLoading: false,
      }))
      .addCase(fetchRentFrequencies.pending, (state) => ({
        ...state,
        isLoading: true,
      }))
      .addCase(fetchRentFrequencies.rejected, (state) => ({
        ...state,
        isLoading: false,
      }))
      .addCase(fetchRentFrequencies.fulfilled, (state, { payload }) => ({
        ...state,
        rentFrequencies: payload,
        isLoading: false,
      }))
      .addCase(fetchTypes.pending, (state) => ({
        ...state,
        isLoading: true,
      }))
      .addCase(fetchTypes.rejected, (state) => ({
        ...state,
        isLoading: false,
      }))
      .addCase(fetchTypes.fulfilled, (state, { payload }) => ({
        ...state,
        types: payload,
        isLoading: false,
      }))
      .addCase(fetchRegion.pending, (state) => ({
        ...state,
        isLoading: true,
      }))
      .addCase(fetchRegion.rejected, (state) => ({
        ...state,
        isLoading: false,
      }))
      .addCase(fetchRegion.fulfilled, (state, { payload }) => ({
        ...state,
        regions: payload,
        isLoading: false,
      }))
      .addCase(fetchCompletedStatuses.pending, (state) => ({
        ...state,
        isLoading: true,
      }))
      .addCase(fetchCompletedStatuses.rejected, (state) => ({
        ...state,
        isLoading: false,
      }))
      .addCase(fetchCompletedStatuses.fulfilled, (state, { payload }) => ({
        ...state,
        completedStatuses: payload,
        isLoading: false,
      }))
      .addCase(fetchFurnishingStatuses.pending, (state) => ({
        ...state,
        isLoading: true,
      }))
      .addCase(fetchFurnishingStatuses.rejected, (state) => ({
        ...state,
        isLoading: false,
      }))
      .addCase(fetchFurnishingStatuses.fulfilled, (state, { payload }) => ({
        ...state,
        furnishingStatuses: payload,
        isLoading: false,
      }))
      .addCase(fetchLocations.pending, (state) => ({
        ...state,
        isLoading: true,
      }))
      .addCase(fetchLocations.rejected, (state) => ({
        ...state,
        isLoading: false,
      }))
      .addCase(fetchLocations.fulfilled, (state, { payload }) => ({
        ...state,
        locations: payload,
        isLoading: false,
      }))
      .addCase(fetchPurposes.pending, (state) => ({
        ...state,
        isLoading: true,
      }))
      .addCase(fetchPurposes.rejected, (state) => ({
        ...state,
        isLoading: false,
      }))
      .addCase(fetchPurposes.fulfilled, (state, { payload }) => ({
        ...state,
        purposes: payload,
        isLoading: false,
      }));
  },
});

export default LibrariesSlice.reducer;
